import React, { useEffect, useState } from "react";

import { Button, IconButton, Menu, MenuItem, Tooltip } from "@mui/material";
import { Done as DoneIcon, MoreVert as MoreVertIcon } from "@mui/icons-material";
import { Typography } from "@mui/material";
import { saveAs } from "file-saver";
import { useTranslation } from "react-i18next";
import { TbDownload } from "react-icons/tb";

import { File as IFile } from "../../../../../models/file";
import { Input } from "../../../../Form";
import ShowPdfModal from "../../../../ShowPdfModal";
import { NotificationToast } from "../../../Toast/NotificationToast";
import useStyles from "./styles";

type Props = {
  file?: IFile;
  onOpenCarouselModal?: (value: boolean) => void;
  onDelete?: () => void;
  onEdit?: (file: IFile) => void;
};

const File = (props: Props) => {
  const { file, onDelete, onEdit, onOpenCarouselModal } = props;

  const { t: translate } = useTranslation();

  const [name, setName] = useState<string>();

  const [anchorMoreIcon, setAnchorMoreIcon] = useState<null | HTMLElement>(null);

  const [isDisabled, setIsDisabled] = useState<boolean>(!!file?.name);

  const [isOpenPdfModal, setIsOpenPdfModal] = useState<boolean>(false);

  const [isDownloading, setIsDownloading] = useState<boolean>(false);

  const classes = useStyles({ isDisabled: isDisabled || isDownloading })();

  useEffect(() => {
    if (file && file?.name) setName(file?.name);
  }, [file]);

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorMoreIcon(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorMoreIcon(null);
  };

  const handleEditName = () => {
    setIsDisabled(!isDisabled);
  };

  const onDownload = async (file?: IFile) => {
    if (file && file.uri && file.name && file.extension) {
      setIsDownloading(true);
      try {
        const uniqueUrl = `${file.uri}?t=${new Date().getTime()}`;

        const response = await fetch(uniqueUrl, {
          method: "GET",
          mode: "cors",
        });

        if (!response.ok) {
          NotificationToast.error(translate("unableToDownload"));
          setIsDownloading(false);
          return;
        }

        const blob = await response.blob();
        saveAs(blob, `${file.name}.${file.extension}`);
        NotificationToast.success(`${translate("fileDownloadComplete")}!`);
      } catch (error) {
        NotificationToast.error(translate("unableToDownload"));
      } finally {
        setIsDownloading(false);
      }
    }
  };

  const confirmChange = () => {
    if (name) {
      handleEditName();
      if (onEdit) onEdit({ name });
      setName(file?.name);
    } else {
      NotificationToast.warning(translate("theFileNameIsRequired"));
    }
  };

  const openFile = () => {
    switch (true) {
      case file?.type?.includes("image"):
        if (onOpenCarouselModal) onOpenCarouselModal(true);
        break;
      case file?.type?.includes("video"):
        if (onOpenCarouselModal) onOpenCarouselModal(true);
        break;
      case file?.type?.includes("pdf"):
        setIsOpenPdfModal(true);
        break;
      default:
        break;
    }
  };

  const getMenuItemsByFile = (file: IFile) => {
    if (file?.isAllowedDelete === false || file?.isAllowedEdit === false)
      return (
        <MenuItem
          key="Download"
          onClick={() => {
            handleCloseMenu();
            onDownload(file);
          }}
        >
          <Typography fontFamily="Manrope" fontWeight={500} fontSize="12px" lineHeight="24px">
            {translate("download")}
          </Typography>
        </MenuItem>
      );

    return (
      <>
        <MenuItem
          key="Rename"
          onClick={() => {
            handleCloseMenu();
            handleEditName();
            if (!isDisabled) setName(file?.name);
          }}
        >
          <Typography fontFamily="Manrope" fontWeight={500} fontSize="12px" lineHeight="24px">
            {translate("rename")}
          </Typography>
        </MenuItem>
        <MenuItem
          key="Download"
          onClick={() => {
            handleCloseMenu();
            onDownload(file);
          }}
        >
          <Typography fontFamily="Manrope" fontWeight={500} fontSize="12px" lineHeight="24px">
            {translate("download")}
          </Typography>
        </MenuItem>
        <MenuItem
          key="Delete"
          onClick={() => {
            handleCloseMenu();
            if (onDelete) onDelete();
          }}
        >
          <Typography
            fontFamily="Manrope"
            fontWeight={500}
            fontSize="12px"
            lineHeight="24px"
            className={classes.textDelete}
          >
            {translate("delete")}
          </Typography>
        </MenuItem>
      </>
    );
  };

  const renderMenuIcon = () => {
    if (!file || !file?.uri) return null;

    return (
      <div>
        <IconButton
          disabled={isDownloading}
          onClick={handleOpenMenu}
          size="small"
          className={classes.iconButton}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
        <Menu
          id="long-menu"
          anchorEl={anchorMoreIcon}
          keepMounted
          open={Boolean(anchorMoreIcon)}
          onClose={handleCloseMenu}
        >
          {getMenuItemsByFile(file)}
        </Menu>
      </div>
    );
  };

  const renderIconButtonConfirmEdit = () => {
    if (!isDisabled && !isDownloading) {
      return (
        <IconButton
          aria-label="more"
          aria-controls="long-menu"
          aria-haspopup="true"
          onClick={confirmChange}
          size="small"
        >
          <DoneIcon fontSize="small" />
        </IconButton>
      );
    }

    return null;
  };

  const renderContent = () => {
    if (isDownloading)
      return <TbDownload className={classes.iconDownload} size="44px" color="#308CDB" />;

    return (
      <Button onClick={openFile} className={classes.file}>
        <span className={classes.textType}>.{file?.extension}</span>
      </Button>
    );
  };

  return (
    <div className={classes.mainDiv}>
      <ShowPdfModal
        url={file?.uri}
        open={isOpenPdfModal}
        handleClose={() => {
          setIsOpenPdfModal(false);
        }}
      />
      <div style={{ position: "relative", height: "100%" }}>
        {renderMenuIcon()}
        <Tooltip title={isDownloading ? translate("downloading") : name || ""} placement="bottom">
          <div style={{ height: "100%" }}>
            {renderContent()}
            <div className={classes.containerInput}>
              <div style={{ width: isDisabled || isDownloading ? "85%" : "70%" }}>
                <Input
                  name="folder"
                  value={isDownloading ? translate("downloading") : name}
                  disabled={isDisabled || isDownloading}
                  className={classes.input}
                  onChange={(event) => setName(event.target.value)}
                />
              </div>
              {renderIconButtonConfirmEdit()}
            </div>
          </div>
        </Tooltip>
      </div>
    </div>
  );
};

export default File;
